import { Link } from "react-router-dom";
import { MyelinaMark } from "@/components/brand/MyelinaMark";
import { BRAND } from "@/lib/brand";
import { useCanonical } from "@/lib/useCanonical";

const Terms = () => {
  useCanonical({ path: "/terms" });

  return (
    <div className="min-h-screen bg-background">
    <div className="container mx-auto px-4 py-12 max-w-3xl">
      <Link to="/" className="flex items-center gap-2 font-heading font-bold text-lg mb-8">
        <MyelinaMark className="h-5 w-5 text-primary" />{BRAND.name}
      </Link>
      <h1 className="font-heading text-3xl font-bold mb-2">Terms of Service</h1>
      <p className="text-muted-foreground mb-8"><strong>Last updated:</strong> April 6, 2026</p>

      <div className="prose prose-sm text-muted-foreground space-y-6 [&_h2]:font-heading [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:font-heading [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground">

        <p>These Terms of Service ("Terms") govern your access to and use of the Myelina Health application and website (the "Service") operated by Myelina Health ("we," "our," or "us"). By creating an account or using the Service, you agree to be bound by these Terms. If you do not agree, please do not use the Service.</p>

        <h2>1. Eligibility</h2>
        <p>You must be at least 16 years of age to use the Service. By using the Service, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.</p>

        <h2>2. Important Medical Disclaimer</h2>
        <p><strong>Myelina Health is a wellness and energy tracking tool. It is NOT a medical device, diagnostic tool, or substitute for professional medical advice, diagnosis, or treatment.</strong></p>
        <p><strong>Myelina</strong>, the in-product AI energy companion, is a software companion who analyses your self-reported check-ins to surface energy patterns. Myelina is not a clinician, not a diagnostic tool, and not a medical device. Her outputs are informational suggestions, not medical advice.</p>
        <ul>
          <li>The Service provides energy pattern analysis and predictions based on self-reported data. These are <strong>informational only</strong> and should never replace consultation with your neurologist, primary care physician, or healthcare team.</li>
          <li>AI-generated insights, Energy Maps, Myelina coaching responses, and rest-day nudges are algorithmic estimations, not clinical assessments.</li>
          <li>Never make medication, treatment, or lifestyle changes based solely on information from this app or from Myelina. Always consult a qualified healthcare professional.</li>
          <li>If you are experiencing a medical emergency, call your local emergency number immediately.</li>
        </ul>

        <h2>3. Account Registration & Security</h2>
        <ul>
          <li>You must provide accurate, current, and complete information when creating your account.</li>
          <li>You are solely responsible for maintaining the confidentiality of your login credentials and for all activity under your account.</li>
          <li>You agree to notify us immediately of any unauthorized access to your account.</li>
          <li>We reserve the right to suspend or terminate accounts that violate these Terms or are used fraudulently.</li>
        </ul>

        <h2>4. Acceptable Use</h2>
        <p>You agree not to:</p>
        <ul>
          <li>Use the Service for any unlawful purpose or in violation of any applicable laws or regulations.</li>
          <li>Attempt to gain unauthorized access to our systems, other user accounts, or data.</li>
          <li>Reverse engineer, decompile, or attempt to extract the source code of the Service.</li>
          <li>Use automated systems (bots, scrapers) to access the Service without prior written consent.</li>
          <li>Upload malicious code, viruses, or any content designed to disrupt the Service.</li>
          <li>Impersonate another person or entity, or misrepresent your affiliation with any person or entity.</li>
          <li>Use the Service to provide medical advice to others or represent yourself as a healthcare professional through the platform.</li>
        </ul>

        <h2>5. Access</h2>
        <p>Myelina Health is currently offered on an invite-only basis to a limited cohort. There are no paid plans, subscriptions, billing cycles, or free trials at this time. If we introduce paid tiers in the future, we will update these Terms and give you clear advance notice before any charges apply.</p>
        <p>You may request an invitation from our website. We may pause, limit, or revoke access at our discretion, consistent with these Terms and applicable law.</p>

        <h2>6. Intellectual Property</h2>
        <ul>
          <li>The Service, including its design, code, branding, logos, text, graphics, and AI models, is owned by Myelina Health and protected by copyright, trademark, and other intellectual property laws.</li>
          <li>You retain ownership of the personal data you enter into the Service. By using the Service, you grant us a limited, non-exclusive license to process your data solely for the purpose of providing and improving the Service.</li>
          <li>"Myelina Health" and "Myelina" are trademarks. You may not use them without prior written permission.</li>
        </ul>

        <h2>7. User-Generated Content</h2>
        <p>If you submit notes, tags, or other text content through the Service:</p>
        <ul>
          <li>You retain ownership of your content.</li>
          <li>You grant us a license to store, process, and display your content to you as part of the Service.</li>
          <li>You are responsible for ensuring your content does not violate any laws or third-party rights.</li>
        </ul>

        <h2>8. AI-Powered Features</h2>
        <ul>
          <li>The Service uses artificial intelligence to generate energy predictions, pattern analysis, and coaching responses.</li>
          <li>AI outputs are probabilistic estimations and may be inaccurate, incomplete, or misleading. They should be treated as suggestions, not facts.</li>
          <li>We do not guarantee the accuracy, reliability, or completeness of any AI-generated content.</li>
          <li>AI features may be rate-limited for fair usage across all users.</li>
        </ul>

        <h2>9. Limitation of Liability</h2>
        <p><strong>TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</strong></p>
        <ul>
          <li>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</li>
          <li>WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.</li>
          <li>IN NO EVENT SHALL MYELINA HEALTH, ITS OFFICERS, DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.</li>
          <li>OUR TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS RELATED TO THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR $100 USD, WHICHEVER IS GREATER.</li>
        </ul>

        <h2>10. Indemnification</h2>
        <p>You agree to indemnify, defend, and hold harmless Myelina Health and its affiliates from any claims, damages, losses, liabilities, and expenses (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your violation of any rights of a third party.</p>

        <h2>11. Service Availability & Modifications</h2>
        <ul>
          <li>We strive to maintain high availability but do not guarantee uninterrupted access. Scheduled maintenance, updates, and unforeseen outages may temporarily affect the Service.</li>
          <li>We reserve the right to modify, suspend, or discontinue any feature of the Service at any time, with or without notice.</li>
          <li>We will make reasonable efforts to notify users of material changes that affect their use of the Service.</li>
        </ul>

        <h2>12. Termination</h2>
        <ul>
          <li>You may terminate your account at any time by deleting it from the Settings page.</li>
          <li>We may suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or if required by law.</li>
          <li>Upon termination, your right to use the Service ceases immediately. Data deletion follows our Privacy Policy.</li>
        </ul>

        <h2>13. Dispute Resolution</h2>
        <ul>
          <li><strong>Informal Resolution:</strong> Before filing any formal claim, you agree to contact us at legal@myelinahealth.com and attempt to resolve the dispute informally for at least 30 days.</li>
          <li><strong>Governing Law:</strong> These Terms are governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to conflict of law principles.</li>
          <li><strong>Arbitration:</strong> Any dispute not resolved informally shall be settled by binding arbitration in accordance with the American Arbitration Association rules. You agree to waive your right to a jury trial and to participate in a class action lawsuit.</li>
        </ul>

        <h2>14. Severability</h2>
        <p>If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.</p>

        <h2>15. Entire Agreement</h2>
        <p>These Terms, together with our Privacy Policy, constitute the entire agreement between you and Myelina Health regarding the Service and supersede all prior agreements, understandings, and representations.</p>

        <h2>16. Changes to These Terms</h2>
        <p>We may update these Terms from time to time. We will notify you of material changes by posting a notice within the app or sending an email to your registered address at least 30 days before changes take effect. Your continued use of the Service after changes become effective constitutes acceptance of the revised Terms.</p>

        <h2>17. Contact Us</h2>
        <p>If you have questions about these Terms, please contact us:</p>
        <ul>
          <li><strong>Email:</strong> legal@myelinahealth.com</li>
          <li><strong>Subject Line:</strong> "Terms Inquiry. [Your Name]"</li>
        </ul>
      </div>
    </div>
  </div>
  );
};

export default Terms;
